import { Component } from '@theme/component';

/**
 * `<size-guide>` — the body of the product page's Size Guide drawer
 * (Figma node 6530:114197, rendered by snippets/size-guide-content.liquid).
 *
 * The chart is not the theme's data. It comes from the Size Guide app's proxy
 * endpoint, `/apps/size-guide/size-guide?handle=…&locale=…`, which answers with
 * `{ html, desktopOpenMode, mobileOpenMode }` — the same endpoint the live theme
 * uses. The two open modes are the app's own advice about how to present its
 * panel and are ignored here: this theme always shows it in a drawer.
 *
 * ## The app contract
 *
 * What that HTML gives us, and what this file relies on:
 *
 * - `.sg-tab-button[data-tab]` and a matching `.sg-tab-panel[data-tab]` per tab.
 *   Footwear returns one ("Size chart"); apparel returns a second, "Find your
 *   size", holding the measurement calculator. The theme redraws the buttons and
 *   moves the panels, so it works for either without knowing which it has.
 * - `.sg-region-panel[data-region]` inside each tab panel, one per size system —
 *   three for footwear, five for apparel — a `table.sg-table` in a chart panel
 *   and a `.sg-grid--calc` of `.sg-field`s plus a `.sg-calc-button` in a calc one.
 * - `.sg-input[data-field-priority]` in a calc field, the key the calculator wants
 *   that measurement under, and a `.sg-input-error-msg` beside it already carrying
 *   its "Required field" text.
 * - `.sg-wrap[data-chart-id]`, the id of the chart the payload was built from,
 *   which the calculate call has to quote back.
 * - `td.sg-cell[data-cm][data-inch]` — both readings, already converted and
 *   rounded by the app. Nothing here does arithmetic on a measurement.
 * - `select.sg-region-select` with an `<option>` per region, which is where the
 *   region list comes from.
 * - Up to two `.sg-accordion` blocks in document order, measurements then fit
 *   advice, each a `.sg-acc-header` over a `.sg-acc-panel` holding a
 *   `.sg-topimage` and a `.sg-toptext`. Apparel sends only the first. Figma draws
 *   them as plain titled sections, so the header becomes a heading here and the
 *   chevron goes.
 * - `.sg-customer-care`, the contact line, whose link the app ships with an
 *   empty `href` for its own script to fill in.
 *
 * ## The calculator
 *
 * "Find your size" posts its measurements to a second endpoint of the same proxy,
 * `/apps/size-guide/size-guide/calculate?locale=…`, which answers with `{ html }`
 * again — the recommended size as a panel of its own:
 *
 * - `[data-sg-result][data-variant-id]` wrapping the lot, the variant written as
 *   a GID.
 * - `.sg-result-image`, the product's photo; `.sg-your-size-label` and
 *   `.sg-your-size-value`, the words and the size; a `.sg-result-field` of
 *   `.sg-result-label` / `.sg-result-value` per measurement it was read off.
 * - `.sg-add-to-cart`, for the app's own script to bind, and either a close
 *   button or a header of its own depending on the `openMode` it was sent. All
 *   three are the app's chrome and none of them is read here.
 *
 * The app's own script would have submitted this form, validated it and swapped
 * the panel; nothing of it runs in this theme, so the submit, the empty-field
 * check and the result view are all this file's.
 *
 * What the answer does not carry is stock, and — on a product whose variants are
 * not sized in the chart's leading column — any variant at all: the app names a
 * size like "3XL", finds nothing called that among trousers sold as "Adult | 52",
 * and returns an empty `data-variant-id`. Both gaps are the theme's to close, and
 * the snippet renders the product's own size run alongside the drawer for it:
 * every size, its variant, and whether that one can be bought. `#sizeCodeFor`
 * reads the missing pairing out of the chart the app has already sent.
 *
 * ## What the theme owns
 *
 * The app's chrome is not Figma's, so the theme renders the tab strip, the
 * Country / Region field and the CM / INCH toggle itself — the toggle in
 * particular is a switch with the labels outside the track, which the app's pair
 * of buttons cannot be restyled into. The app's own controls and header are
 * dropped, and its content nodes are moved into the theme's slots. Same division
 * of labour as edd-drawer.js: we own the presentation, the app owns the content.
 *
 * The exception is Add to cart. Figma ends the result on the theme's primary CTA
 * (node 6350:76935), and every other add in the theme goes through
 * `<product-form-component>` — so that CTA is rendered by the snippet and this
 * file only fills in the variant the app recommended.
 *
 * Fetched once, on first open, and kept: the chart does not change while the
 * shopper is on the page.
 */
class SizeGuide extends Component {
  /**
   * A fit heading as the app writes it: the letter that labels the fit in the
   * size chart, then the name. Only the name is shown.
   */
  static #FIT_CODE = /^\s*\S{1,3}\s*\|\s*(.+?)\s*$/;

  /**
   * Trims a "W | Wide" heading to "Wide", leaving every other paragraph — and
   * the heading's own emphasis — alone.
   *
   * @param {Element} node
   * @returns {Element}
   */
  static #nameFit(node) {
    const heading = node.querySelector('strong') ?? node;
    const named = SizeGuide.#FIT_CODE.exec(heading.textContent ?? '');

    if (named?.[1]) heading.textContent = named[1];

    return node;
  }

  /** One word of a label, as a first letter and whatever follows it. */
  static #WORD = /(\p{L})(\p{L}*)/gu;

  /**
   * The unit a chart heading carries, as the app writes it: "A. FOOT LENGTH
   * (CM)". The app only ever labels headings in centimetres, so #setUnit swaps
   * it for inches from the heading's original text rather than parsing both.
   */
  static #CM_LABEL = /\(\s*cm\s*\)/i;

  /** The class a tapped chart cell carries while its crosshair is drawn. */
  static #SELECTED_CELL = 'size-guide__cell--selected';

  /** The numeric id at the tail of a `gid://shopify/ProductVariant/…`. */
  static #VARIANT_ID = /(\d+)$/;

  /**
   * A chart cell's canonical value: the centimetre reading the app wrote, not the
   * cell's text, which #setUnit rewrites when the shopper flips to inches. A size
   * reads the same in both; a measurement does not.
   *
   * @param {Element} cell
   * @returns {string}
   */
  static #reading(cell) {
    const reading = cell instanceof HTMLElement ? cell.dataset.cm : undefined;

    return (reading ?? cell.textContent ?? '').trim();
  }

  /**
   * "A. CHEST" -> "A. Chest". The app shouts its calculator's labels where Figma
   * capitalises them (node 6350:76882), and `text-transform: capitalize` cannot
   * get there — it raises first letters without lowering anything else, so an
   * all-caps label comes out unchanged. Word by word, so "C. LOW HIP" reads
   * "C. Low Hip", and idempotent, so a label the app one day sends already
   * capitalised is left as it is.
   *
   * @param {string} text
   * @returns {string}
   */
  static #capitalize(text) {
    return text.replace(SizeGuide.#WORD, (_word, first, rest) => first.toUpperCase() + rest.toLowerCase());
  }

  /**
   * The product photo at the size Figma draws it, rather than at whatever size it
   * was uploaded at: the app hands over a bare CDN URL, and a 2000px original in
   * a 243px tile is a megabyte for nothing. 2x, for the same reason every
   * `image_url` in the theme asks for one.
   *
   * @param {string} src
   * @returns {string}
   */
  static #sized(src) {
    try {
      const url = new URL(src, document.baseURI);
      if (!url.hostname.endsWith('cdn.shopify.com')) return src;

      url.searchParams.set('width', '486');

      return url.href;
    } catch {
      // Not a URL this can reason about — the app's own string still works as one.
      return src;
    }
  }

  /** The tab on show, as the app names it in `data-tab` ('chart' / 'calc'). */
  /** @type {string | undefined} */
  #tab;

  /** @type {Promise<void> | undefined} */
  #loaded;

  /**
   * The chart the payload was built from, which the calculate call has to quote
   * back. Read off the app's own wrapper, the one node of its chrome this needs.
   * @type {string | undefined}
   */
  #chartId;

  /** Guards the calculator against a second submit while the first is in flight. */
  #calculating = false;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    const drawer = this.closest('theme-drawer');
    if (!drawer) return;

    // Rebuilt per connection, like care-guide-video.js: a controller made at
    // construction would already be aborted for an element the section morph
    // re-inserts, and the drawer would never load again.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    drawer.addEventListener('theme-drawer:open', this.#load, { signal });

    // Every open starts at the top of the panel.
    //
    // One drawer serves both links: "Size guide" opens it as it stands, "Fit guide"
    // opens it already scrolled down to the fit advice (`openAtFitAdvice`). The
    // scroller is this element, and it keeps its offset between opens — the close
    // reset below puts the *view* back without touching it — so a shopper who read
    // the fit advice and later asked for the size chart got the panel back exactly
    // where they had left it, with the chart they came for above the fold.
    //
    // This does not fight `openAtFitAdvice`: the event is dispatched synchronously
    // from the drawer's `open()`, and that method scrolls only after awaiting the
    // chart, so the reset has always run by then.
    drawer.addEventListener('theme-drawer:open', this.#scrollToTop, { signal });

    // Bound here rather than declared with `on:change` in the markup: the input
    // belongs to snippets/toggle.liquid, so the attribute would have to be passed
    // in as one of its parameters — which is exactly how this wiring went missing
    // once already, silently, with the toggle still rendering and animating.
    this.#unitInput?.addEventListener('change', this.#setUnit, { signal });

    // Touch has no hover to light up the crosshair the stylesheet draws, so a
    // tap selects a cell instead. Delegated from here because the tables are the
    // app's, replaced wholesale on every load.
    this.addEventListener('click', this.#selectCell, { signal });

    // Figma gives the calculator's answer the whole panel, with no way back in
    // it, so closing the drawer is the way back — reset on the dialog's own
    // `close` rather than on `theme-drawer:close`, which fires at the *start* of
    // the slide out and would play the swap on screen. Same beat
    // country-selector-overlay.js resets on, for the same reason.
    this.closest('dialog')?.addEventListener('close', this.#reset, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @returns {HTMLInputElement | null} */
  get #unitInput() {
    const input = this.querySelector('[data-size-guide-unit] input');

    return input instanceof HTMLInputElement ? input : null;
  }

  /** Shows the selected region. Bound from the theme's select with `on:change`. */
  selectRegion() {
    this.#applyVisibility();
  }

  /**
   * Switches every table between centimetres and inches — checked is inches,
   * which is the toggle's own Option A / Option B reading.
   *
   * Reads the input rather than an event, so the change listener and the pass
   * that runs once the chart lands are the same call.
   *
   * Both readings are attributes the app already wrote, so this only ever
   * chooses between two strings. No rounding, and so no way for repeated
   * toggling to walk a value away from itself.
   *
   * Headings that name the unit follow suit, "(CM)" to the storefront language's
   * word for inches ("(IN)", "(ZOLL)", …, `size_guide.unit_inch_heading`) and
   * back, always rewritten from the text the app sent, kept on the heading as
   * `data-cm-label`.
   */
  #setUnit = () => {
    const input = this.#unitInput;
    if (!input) return;

    const unit = input.checked ? 'inch' : 'cm';
    this.dataset.unit = unit;

    for (const cell of this.querySelectorAll('.sg-cell')) {
      if (!(cell instanceof HTMLElement)) continue;

      const reading = cell.dataset[unit];
      if (reading != null) cell.textContent = reading;
    }

    const inchLabel = this.dataset.inchHeadingLabel || 'IN';

    for (const heading of this.querySelectorAll('table.sg-table th')) {
      if (!(heading instanceof HTMLElement)) continue;

      const label = (heading.dataset.cmLabel ??= heading.textContent ?? '');
      if (!SizeGuide.#CM_LABEL.test(label)) continue;

      heading.textContent = unit === 'inch' ? label.replace(SizeGuide.#CM_LABEL, `(${inchLabel})`) : label;
    }
  };

  /**
   * Marks the tapped chart cell as selected, or clears it when it already was:
   * the touch stand-in for the hover crosshair (see snippets/size-guide-styles.liquid,
   * which only draws the class where there is no hover). One cell at a time.
   *
   * @param {MouseEvent} event
   */
  #selectCell = (event) => {
    const cell = event.target instanceof Element ? event.target.closest('table.sg-table td') : null;
    if (!cell || !this.contains(cell)) return;

    const selected = cell.classList.contains(SizeGuide.#SELECTED_CELL);
    this.#clearSelectedCell();
    if (!selected) cell.classList.add(SizeGuide.#SELECTED_CELL);
  };

  #clearSelectedCell() {
    for (const cell of this.querySelectorAll(`.${SizeGuide.#SELECTED_CELL}`)) {
      cell.classList.remove(SizeGuide.#SELECTED_CELL);
    }
  }

  /** Puts the panel back at its top. Instant: the drawer is still sliding in. */
  #scrollToTop = () => {
    this.scrollTop = 0;
  };

  /**
   * Scrolls down to the measuring section. Bound from the link with `on:click`.
   *
   * `scrollIntoView` needs no offset arithmetic here, unlike the jump in
   * product-more-details.js: this element is the scroll container, the drawer's
   * header sits outside it, and nothing above the content is sticky.
   */
  jumpToMeasurements() {
    const section = this.refs.measurements;

    if (section instanceof HTMLElement) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** @returns {Promise<void>} resolves once the chart is in the slots */
  #load = () => (this.#loaded ??= this.#fetchContent());

  /**
   * Opens the drawer already scrolled to the fit advice. Bound from the "Fit
   * guide" link, which shares this one drawer with the "Size guide" link —
   * they are two ways into the same panel, not two panels.
   *
   * Waits for the content: on a first open there is nothing to scroll to until
   * the app has answered, and `#load` resolves exactly then. On a later open it
   * settles in a microtask, while the panel is still sliding in, which the
   * scroll does not depend on — the scroller is this element, not the panel.
   */
  async openAtFitAdvice() {
    const drawer = this.closest('theme-drawer');
    if (drawer && 'open' in drawer && typeof drawer.open === 'function') drawer.open();

    await this.#load();

    // Nothing to travel to on a product whose guide carries no fit advice —
    // apparel sends none — so the drawer simply opens at the top.
    const section = this.refs.fitAdvice;
    if (section instanceof HTMLElement && !section.hidden) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  async #fetchContent() {
    const endpoint = this.dataset.endpoint;
    if (!endpoint) return;

    this.dataset.state = 'loading';

    try {
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(`Size guide responded ${response.status}`);

      const { html } = await response.json();
      if (typeof html !== 'string' || html.trim() === '') throw new Error('Size guide returned no content');

      this.#adopt(html);
      this.dataset.state = 'ready';
    } catch (error) {
      // Kept rather than swallowed: the drawer shows its own message, and this is
      // the only place the reason is visible when the app or the proxy is down.
      console.error('[size-guide]', error);

      // Re-armed so the next open tries again — a failed fetch is usually the
      // network rather than the product.
      this.#loaded = undefined;
      this.dataset.state = 'error';
    }
  }

  /**
   * Parses the app's HTML and moves the nodes we present into the theme's
   * slots. Parsed into a `<template>` first: its content is inert, so nothing
   * in the payload loads or runs while we pick through it.
   *
   * @param {string} html
   */
  #adopt(html) {
    const template = document.createElement('template');
    template.innerHTML = html;
    const source = template.content;

    this.#chartId = source.querySelector('.sg-wrap')?.getAttribute('data-chart-id') ?? undefined;

    // The accordions come out first, and this order is load-bearing: footwear
    // nests its fit advice *inside* the chart tab panel, so moving the panels
    // ahead of this would carry that accordion off with them — leaving it inside
    // the chart, above the measuring section, still wearing the app's own header
    // and chevron. #buildSection excises each one from `source` as it takes it.
    //
    // Measurements first in the payload, which is also the order Figma stacks
    // them. Apparel sends no fit advice, and the slot for it stays hidden.
    const [measurements, fitAdvice] = source.querySelectorAll('.sg-accordion');

    const measurePanel = this.#buildSection(this.refs.measurements, measurements);
    if (measurePanel) this.#liftMeasureLabels(measurePanel);

    const fitPanel = this.#buildSection(this.refs.fitAdvice, fitAdvice);
    if (fitPanel) this.#splitFits(fitPanel);

    // Whole tab panels rather than the region panels inside them: apparel sends a
    // chart panel and a calculator panel, and moving them individually would pile
    // both into one view — which is what put the "Find your size" fields under
    // the chart.
    const panels = this.refs.panels;
    if (panels instanceof HTMLElement) panels.append(...source.querySelectorAll('.sg-tab-panel[data-tab]'));

    // Every other region's chart is missing an EU reading (BAS-209): the app
    // renders one whole table per system, and switching region swaps which
    // table is on screen rather than adding a column to it. Splicing the EU
    // system's own column into the other tables, row for row, keeps the EU
    // size on screen whatever region the shopper has picked.
    this.#addEuColumn();

    // After the panels, not before: the fields it dresses arrive inside them.
    this.#dressCalculator();

    this.#buildTabs(source);

    this.#fillSlot(this.refs.care, source.querySelector('.sg-customer-care'));
    this.#fillRegionOptions(source);
    this.#wireCareLink();

    // Applies whichever unit and region the controls are already showing, so a
    // shopper who flipped the toggle while the fetch was in flight does not see
    // it land on the other reading.
    this.#applyControls();
  }

  /**
   * Redraws the app's tab buttons as the theme's strip: the words are the app's
   * (already translated for the locale the endpoint was asked for), the markup
   * and the rule under the active one are Figma's.
   *
   * Listeners are attached here rather than declared in the markup because these
   * buttons do not exist until the chart lands.
   *
   * @param {DocumentFragment} source
   */
  #buildTabs(source) {
    const strip = this.refs.tabs;
    if (!(strip instanceof HTMLElement)) return;

    const buttons = source.querySelectorAll('.sg-tab-button[data-tab]');

    for (const button of buttons) {
      if (!(button instanceof HTMLElement)) continue;

      const { tab } = button.dataset;
      if (!tab) continue;

      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'size-guide__tab cta1--typography';
      item.dataset.tab = tab;
      item.textContent = button.textContent?.trim() ?? '';
      item.addEventListener('click', this.#selectTab);

      strip.append(item);

      // Whichever the app opened on, which is its first.
      this.#tab ??= tab;
      if (button.classList.contains('sg-tab-button--active')) this.#tab = tab;
    }

    // A single tab is a heading, not a choice: it keeps Figma's rule but stops
    // offering a press that goes nowhere.
    strip.dataset.count = String(strip.children.length);
    strip.hidden = strip.children.length === 0;
  }

  /**
   * @param {Event} event
   */
  #selectTab = (event) => {
    const { currentTarget } = event;
    if (!(currentTarget instanceof HTMLElement) || !currentTarget.dataset.tab) return;

    this.#tab = currentTarget.dataset.tab;
    this.#applyVisibility();
  };

  /**
   * Moves one of the app's accordions into a slot as a plain titled section:
   * its header's words become a heading, and its chevron — which had a control
   * to belong to — goes with the button.
   *
   * @param {Element | Element[] | undefined} slot
   * @param {Element | undefined} accordion
   * @returns {HTMLElement | null} the section's body, for a caller that has more to do with it
   */
  #buildSection(slot, accordion) {
    if (!(slot instanceof HTMLElement) || !accordion) return null;

    const title = accordion.querySelector('.sg-acc-header')?.textContent?.trim();
    if (title) {
      const heading = document.createElement('p');
      heading.className = 'size-guide__section-title cta1--typography';
      heading.textContent = title;
      slot.append(heading);
    }

    const panel = accordion.querySelector('.sg-acc-panel');
    if (panel instanceof HTMLElement) slot.append(panel);

    // What is left of the block goes with it, header and chevron included. The
    // app wraps each accordion in a `.sg-topimage-container`, and for footwear's
    // fit advice that wrapper sits inside the chart tab panel — which is moved
    // wholesale further down, and would otherwise bring the husk along.
    (accordion.closest('.sg-topimage-container') ?? accordion).remove();

    slot.hidden = false;

    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * Puts each measurement's name on a line of its own above its instructions.
   *
   * The app writes every pair as one paragraph — `<strong>A. CHEST</strong>:
   * Measure the circumference…` — where Figma has two lines, the name in the same
   * weight as the copy below it and no gap between them. So each `<strong>`
   * becomes a plain block and the `: ` the app separates them with goes, since it
   * would otherwise open the second line.
   *
   * Footwear sends one of these, apparel one per measurement, which is also what
   * decides the panel's layout: Figma sets a lone diagram beside its instruction
   * and stands a shared one above the list it explains.
   *
   * @param {HTMLElement} panel
   */
  #liftMeasureLabels(panel) {
    for (const strong of panel.querySelectorAll('.sg-toptext strong')) {
      const label = document.createElement('span');
      label.className = 'size-guide__measure-label';
      label.textContent = strong.textContent?.trim() ?? '';
      strong.replaceWith(label);

      const next = label.nextSibling;
      if (next?.nodeType === Node.TEXT_NODE && next.textContent) {
        next.textContent = next.textContent.replace(/^\s*:\s*/, '');
      }
    }

    const paragraphs = panel.querySelectorAll('.sg-toptext > p').length;
    panel.classList.toggle('size-guide__measure--stacked', paragraphs > 1);
  }

  /**
   * Rebuilds the fit advice panel as one row per fit, each with its own foot
   * beside its copy and a rule between them (Figma node 6530:114264).
   *
   * The app sends all of the fits as a single block: one `.sg-toptext` whose
   * paragraphs run fit after fit, separated by an empty spacer paragraph, and
   * one `.sg-topimage` holding every foot in a single strip. So the copy is cut
   * on those spacers and the strip is shown once per row, cropped to that row's
   * foot — see `.size-guide__fit-image` for the crop.
   *
   * A panel with no spacers is a single fit, and its image is not a strip but
   * one whole illustration (the Rodeo boot's heel-slip drawing, for one). The
   * crop would cut it, so such a panel is left as the app sent it, its image
   * shown whole like the measuring diagram's.
   *
   * Every paragraph the app sent is kept, but its fit heading is trimmed to the
   * fit's name: the app writes it as "W | Wide", pairing the letter that appears
   * in the size chart with the word, and only the word is wanted here.
   *
   * @param {HTMLElement} panel
   */
  #splitFits(panel) {
    const text = panel.querySelector('.sg-toptext');
    const image = panel.querySelector('.sg-topimage img');
    if (!text) return;

    /** @type {Element[][]} */
    const groups = [[]];
    for (const node of [...text.children]) {
      // The app's spacer: a paragraph with nothing in it but a <br>.
      if (node.tagName === 'P' && node.textContent?.trim() === '') {
        groups.push([]);
        continue;
      }

      groups[groups.length - 1]?.push(node);
    }

    const fits = groups.filter((group) => group.length > 0);
    if (fits.length < 2) return;

    const list = document.createElement('div');
    list.className = 'size-guide__fits';
    if (image instanceof HTMLImageElement) list.style.setProperty('--fit-sprite', `url("${image.src}")`);
    list.style.setProperty('--fit-count', String(fits.length));

    fits.forEach((group, index) => {
      const row = document.createElement('div');
      row.className = 'size-guide__fit';

      const foot = document.createElement('span');
      foot.className = 'size-guide__fit-image';
      // 0% / 50% / 100% for three feet — the standard sprite step, which lands
      // each row on its own column of the strip.
      foot.style.setProperty('--fit-offset', `${fits.length > 1 ? (index / (fits.length - 1)) * 100 : 0}%`);
      row.append(foot);

      const copy = document.createElement('div');
      copy.className = 'size-guide__fit-text';
      copy.append(...group.map(SizeGuide.#nameFit));
      row.append(copy);

      list.append(row);
    });

    panel.replaceChildren(list);
  }

  /**
   * @param {Element | Element[] | undefined} slot
   * @param {Element | null | undefined} content
   */
  #fillSlot(slot, content) {
    if (!(slot instanceof HTMLElement) || !content) return;

    slot.append(content);
    slot.hidden = false;
  }

  /**
   * Copies the app's region list into the theme's select. The app is the only
   * thing that knows which size systems a product's chart carries, and its own
   * select is dropped with the rest of its chrome.
   *
   * @param {DocumentFragment} source
   */
  #fillRegionOptions(source) {
    const select = this.refs.regionSelect;
    const appSelect = source.querySelector('.sg-region-select');
    if (!(select instanceof HTMLSelectElement) || !(appSelect instanceof HTMLSelectElement)) return;

    for (const option of appSelect.options) {
      // Selected as a property, never as an attribute: `option[selected]` is how
      // variant-picker.js hunts for its own state, and an option of ours wearing
      // it would be read as a variant option with no id — the throw that moving
      // these drawers out of the picker also guards against. Belt and braces,
      // and nothing here needs the attribute anyway.
      select.append(
        new Option(option.textContent?.trim() ?? option.value, option.value, false, option.defaultSelected),
      );
    }

    // One region is not a choice — Figma's field only earns its space when the
    // chart offers more than one.
    const field = select.closest('[data-size-guide-field]');
    if (field instanceof HTMLElement) field.hidden = select.options.length < 2;
  }

  /**
   * Points the app's contact link at the theme's contact URL. The app ships it
   * with a bare `href` for its own script to fill in; with no URL set the link
   * is unwrapped to its text rather than left as a link to nowhere.
   */
  #wireCareLink() {
    const link = this.querySelector('.sg-link');
    if (!(link instanceof HTMLAnchorElement)) return;

    const url = this.dataset.contactUrl;

    if (url) {
      link.href = url;
    } else {
      link.replaceWith(...link.childNodes);
    }
  }

  #applyControls() {
    this.#applyVisibility();
    this.#setUnit();
  }

  /**
   * Shows the one panel the two controls agree on: the active tab's, and inside
   * it the selected region's. Both dimensions in one pass, because either
   * control moving changes which single panel is on screen.
   *
   * Also mirrored onto the root as `data-tab`, which is how the stylesheet knows
   * to put the unit toggle away on a tab that has no measurements to convert.
   */
  #applyVisibility() {
    // A selection belongs to the table it was made in; another tab or region
    // starts clean.
    this.#clearSelectedCell();

    const region = this.refs.regionSelect instanceof HTMLSelectElement ? this.refs.regionSelect.value : undefined;

    if (this.#tab) this.dataset.tab = this.#tab;

    for (const tab of this.refs.tabs instanceof HTMLElement ? this.refs.tabs.children : []) {
      tab.classList.toggle('size-guide__tab--active', tab instanceof HTMLElement && tab.dataset.tab === this.#tab);
    }

    for (const panel of this.querySelectorAll('.sg-tab-panel[data-tab]')) {
      if (!(panel instanceof HTMLElement)) continue;

      panel.style.removeProperty('display');
      panel.hidden = Boolean(this.#tab) && panel.dataset.tab !== this.#tab;
    }

    if (!region) return;

    for (const panel of this.querySelectorAll('.sg-region-panel[data-region]')) {
      if (!(panel instanceof HTMLElement)) continue;

      // The app ships these with an inline `display` of its own, which would
      // outrank the [hidden] the theme's stylesheet leans on.
      panel.style.removeProperty('display');
      panel.hidden = panel.dataset.region !== region;
    }
  }

  /**
   * Splices the EU system's own column into every other region's chart table
   * in the same tab, row for row. The app renders one whole table per
   * region rather than one shared table with a column per system, so
   * keeping the EU reading on screen regardless of region means copying its
   * column across the other tables, not just showing its panel.
   *
   * Column position and wording both come from the tables themselves: in a
   * tab where every region's table has the same columns bar one, that one
   * is the table's own region column, found by diffing rather than assumed
   * at a fixed index — footwear's chart has no lead column shared across
   * regions the way apparel's "SIZE" does. The EU column lands right after
   * it, wearing whatever header text the EU table already gives it, since
   * that wording is the app's own translation, not one coined here.
   */
  #addEuColumn() {
    for (const tabPanel of this.querySelectorAll('.sg-tab-panel[data-tab]')) {
      const euTable = tabPanel.querySelector('.sg-region-panel--chart[data-region="EU"] table.sg-table');
      if (!(euTable instanceof HTMLTableElement)) continue;

      const euHeader = euTable.tHead?.rows[0];
      const euRows = euTable.tBodies[0]?.rows;
      if (!euHeader || !euRows) continue;

      for (const panel of tabPanel.querySelectorAll('.sg-region-panel--chart[data-region]')) {
        if (panel.getAttribute('data-region') === 'EU') continue;

        const table = panel.querySelector('table.sg-table');
        if (!(table instanceof HTMLTableElement)) continue;

        const header = table.tHead?.rows[0];
        const rows = table.tBodies[0]?.rows;
        if (!header || !rows || rows.length !== euRows.length) continue;

        const index = SizeGuide.#firstDifferingCell(header, euHeader);
        if (index < 0) continue;

        header.cells[index].after(euHeader.cells[index].cloneNode(true));

        for (let i = 0; i < rows.length; i++) {
          rows[i].cells[index].after(euRows[i].cells[index].cloneNode(true));
        }
      }
    }
  }

  /**
   * The first cell index at which two header rows disagree, or -1 if they
   * match throughout or have different lengths — either way, nothing this
   * can safely call "the" region column.
   *
   * @param {HTMLTableRowElement} a
   * @param {HTMLTableRowElement} b
   * @returns {number}
   */
  static #firstDifferingCell(a, b) {
    if (a.cells.length !== b.cells.length) return -1;

    for (let i = 0; i < a.cells.length; i++) {
      if (a.cells[i].textContent?.trim() !== b.cells[i].textContent?.trim()) return i;
    }

    return -1;
  }

  /* ------------------------------------------------------------------
     "Find your size": the app's calculator, submitted and answered here because
     the app's own script — which does all of this on the live theme — is not one
     of the things this theme loads.
     ------------------------------------------------------------------ */

  /**
   * Readies the calculator's fields and gives its button a click, once the tab
   * panels holding them are in the theme's slots.
   *
   * Three passes over markup that arrives ready for a script this theme does not
   * run. The labels come shouted, where Figma capitalises them. Every field comes
   * with its "Required field" message already written out — a complaint about a
   * field nobody has been asked to fill in yet — so `aria-invalid` is made the
   * one thing that reveals it (see the snippet's stylesheet), which also means it
   * clears the moment the shopper starts typing. And the button has no click of
   * its own.
   *
   * Listeners go on without the abort signal, like the tab strip's: these nodes
   * belong to this element, so a listener on one dies with it rather than
   * outliving a disconnect — and a signal from a controller the next connection
   * replaces would silently unbind them all.
   */
  #dressCalculator() {
    for (const field of this.querySelectorAll('.sg-field')) {
      const label = field.querySelector('.sg-field-label');
      if (label) label.textContent = SizeGuide.#capitalize(label.textContent ?? '');

      const input = field.querySelector('.sg-input');
      if (!(input instanceof HTMLInputElement)) continue;

      // The one piece of the app's own palette that reaches the theme: it writes
      // its slate #334155 inline on every input, where the rest of its colours
      // ride on `.sg-wrap`, which is never adopted — and it was quietly beating
      // the `color: inherit` the stylesheet already asks for, so what a shopper
      // typed came out in the app's blue rather than the brand's. Dropped here
      // rather than overridden: an inline style is out of reach of a stylesheet
      // that may not use `!important`.
      input.style.removeProperty('color');

      // A placeholder of one space, which shows nothing, so that these fields
      // rest in the grey border the region select above them wears
      // (--color-input-border, as Figma draws them in node 6350:76883).
      //
      // base.css gives a focused *or filled* input the brand border instead, and
      // reads "filled" as `:not(:placeholder-shown)` — which an input carrying no
      // placeholder at all matches whether or not anything has been typed into
      // it, and at a weight the drawer's own `border-color` cannot outrank. So
      // every one of these came up already wearing the active border. Supplying
      // the placeholder that rule assumes is what makes it mean what it says,
      // and it leaves the whole cascade — grey at rest, brand on focus and once
      // filled, red while invalid — working here exactly as it does on every
      // other input in the theme. Restating the colours instead would have meant
      // rebuilding all four states, focus included, and that rule drops the
      // outline: the border is the only focus affordance these fields have.
      input.placeholder = ' ';

      const message = field.querySelector('.sg-input-error-msg');
      if (message && input.id) {
        // Described rather than labelled, and only ever while it is on screen:
        // the message is `display: none` until the field is invalid, and a hidden
        // description is not read out.
        message.id = `${input.id}-error`;
        input.setAttribute('aria-describedby', message.id);
      }

      input.addEventListener('input', () => input.removeAttribute('aria-invalid'));
    }

    // One per region panel — the app repeats the whole calculator per size
    // system, and gives every copy of the button the same id, so the class is the
    // only way to find them all.
    for (const button of this.querySelectorAll('.sg-calc-button')) {
      button.addEventListener('click', this.#calculate);
    }
  }

  /**
   * Sends the measurements the shopper typed and shows what comes back.
   *
   * @param {Event} event
   */
  #calculate = async (event) => {
    // Captured up front: `currentTarget` is cleared once dispatch returns, and
    // this awaits.
    const { currentTarget: button } = event;
    if (!(button instanceof HTMLElement)) return;

    const panel = button.closest('.sg-region-panel');
    if (!(panel instanceof HTMLElement)) return;

    const measurements = this.#readMeasurements(panel);
    if (!measurements || this.#calculating) return;

    this.#calculating = true;
    button.setAttribute('aria-busy', 'true');
    this.#showFailure(false);

    try {
      const endpoint = this.dataset.calculateEndpoint;
      const handle = this.dataset.handle;

      // Inside the try so that a payload whose wrapper this no longer recognises
      // fails the way a dead endpoint does — with a line the shopper can read,
      // rather than a press that does nothing at all.
      if (!endpoint || !handle || !this.#chartId) {
        throw new Error('Size guide calculator has no endpoint, handle or chart id to ask with');
      }

      const body = new FormData();
      body.set('region', panel.dataset.region ?? '');
      body.set('measurements', JSON.stringify(measurements));
      body.set('chartId', this.#chartId);
      body.set('handle', handle);
      // The app's own advice about how to present the answer, which the endpoint
      // reads only to pick which chrome to wrap it in — a close button for
      // 'modal', a header of its own for 'drawer'. Both are dropped below, so
      // this is 'modal' for the one reason left: it is what the live theme sends.
      body.set('openMode', 'modal');

      const response = await fetch(endpoint, { method: 'POST', body });
      if (!response.ok) throw new Error(`Size guide calculator responded ${response.status}`);

      const { html } = await response.json();
      if (typeof html !== 'string' || html.trim() === '') throw new Error('Size guide calculator returned no result');

      this.#showResult(html);
    } catch (error) {
      // Kept rather than swallowed, like the chart's own fetch: the drawer says
      // only that it did not work, and this is where the reason is.
      console.error('[size-guide]', error);
      this.#showFailure(true);
    } finally {
      this.#calculating = false;
      button.removeAttribute('aria-busy');
    }
  };

  /**
   * The measurements a region's fields hold, keyed the way the calculator asks
   * for them: by each field's `data-field-priority`, which is the order that
   * measurement's column runs in the chart.
   *
   * Answers with nothing if a field is empty, having marked every one that is —
   * there is no point asking the app, which answers an incomplete set with a
   * blank size rather than an error.
   *
   * @param {HTMLElement} panel
   * @returns {Record<string, string> | null}
   */
  #readMeasurements(panel) {
    /** @type {Record<string, string>} */
    const measurements = {};

    /** @type {HTMLInputElement | null} */
    let firstEmpty = null;

    for (const input of panel.querySelectorAll('.sg-input')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const value = input.value.trim();

      if (value === '') {
        input.setAttribute('aria-invalid', 'true');
        firstEmpty ??= input;
        continue;
      }

      input.removeAttribute('aria-invalid');

      const key = input.dataset.fieldPriority;
      if (key) measurements[key] = value;
    }

    if (firstEmpty) {
      firstEmpty.focus();
      return null;
    }

    return measurements;
  }

  /**
   * Draws the app's answer in Figma's clothes (nodes 6350:76907 desktop,
   * 6530:115033 mobile).
   *
   * Rebuilt rather than adopted, the way #buildTabs and #splitFits rebuild the
   * rest: the app sends the photo and the size side by side in a panel of its
   * own, Figma stands them one way on a phone and another on desktop, and the
   * button it ends on is the theme's. The words are still the app's, translated
   * for the locale the endpoint was asked for — only the CTA's are ours.
   *
   * @param {string} html
   */
  #showResult(html) {
    const slot = this.refs.result;
    if (!(slot instanceof HTMLElement)) return;

    const template = document.createElement('template');
    template.innerHTML = html;
    const source = template.content;

    const fragment = document.createDocumentFragment();

    const image = source.querySelector('.sg-result-image');
    if (image instanceof HTMLImageElement) {
      const media = document.createElement('div');
      media.className = 'size-guide__result-media';

      const photo = document.createElement('img');
      photo.className = 'size-guide__result-image';
      // The attribute, not the property: the property has already been resolved
      // against this document, and #sized wants the app's own string.
      photo.src = SizeGuide.#sized(image.getAttribute('src') ?? '');
      photo.alt = image.getAttribute('alt') ?? '';
      photo.width = 213;
      photo.height = 213;

      media.append(photo);
      fragment.append(media);
    }

    const summary = document.createElement('div');
    summary.className = 'size-guide__result-summary';

    const heading = document.createElement('div');
    heading.className = 'size-guide__result-heading';

    const title = source.querySelector('.sg-your-size-label')?.textContent?.trim();
    if (title) {
      const label = document.createElement('p');
      label.className = 'size-guide__result-title';
      label.textContent = title;
      heading.append(label);
    }

    // The app answers an unmatched set with "-" rather than an empty string, so
    // there is always a size to show even when there is no variant to add.
    const size = source.querySelector('.sg-your-size-value')?.textContent?.trim();
    if (size) {
      const value = document.createElement('p');
      value.className = 'size-guide__result-size h3';
      value.textContent = size;
      heading.append(value);
    }

    summary.append(heading);

    const fields = document.createElement('dl');
    fields.className = 'size-guide__result-fields';

    for (const field of source.querySelectorAll('.sg-result-field')) {
      const row = document.createElement('div');
      row.className = 'size-guide__result-field';

      const name = document.createElement('dt');
      name.className = 'size-guide__result-label';
      // Trimmed because the app ends the name with a space where Figma ends it
      // with a colon, which the stylesheet writes.
      name.textContent = field.querySelector('.sg-result-label')?.textContent?.trim() ?? '';

      const reading = document.createElement('dd');
      reading.className = 'size-guide__result-value';
      reading.textContent = field.querySelector('.sg-result-value')?.textContent?.trim() ?? '';

      row.append(name, reading);
      fields.append(row);
    }

    if (fields.children.length > 0) summary.append(fields);

    fragment.append(summary);

    slot.replaceChildren(fragment);
    slot.hidden = false;

    this.#armAddToCart(source.querySelector('[data-sg-result]'));

    this.dataset.view = 'result';

    // The button the shopper pressed is in the view this just put away, and
    // hiding a focused element drops focus back to the dialog — so a keyboard or
    // a screen reader would land at the top of the drawer with no word of what
    // changed. Moved to the answer instead, which is why the slot is focusable.
    slot.focus({ preventScroll: true });

    // The panel submitted from may have been scrolled down; the result is a view
    // of its own and starts at its top.
    this.scrollTop = 0;
  }

  /**
   * Points the theme's own Add to cart at the size the app recommended.
   *
   * Mostly all the CTA needs is the variant: it is a `<product-form-component>`
   * the snippet renders, so the submit, the spinner, the cart events and the cart
   * drawer that opens after are the theme's own, exactly as on the product page.
   * The app writes the variant as a GID and `/cart/add` takes the numeric id.
   *
   * The rest is stock. A recommendation the shopper cannot act on is worse than
   * no button, so a size that is out of stock leaves the CTA disabled and wearing
   * its other label — both of which were rendered with it.
   *
   * @param {Element | null} wrap - the app's result wrapper, which carries it
   */
  #armAddToCart(wrap) {
    const actions = this.refs.resultActions;
    if (!(actions instanceof HTMLElement)) return;

    const input = actions.querySelector('input[name="id"]');
    const recommended = this.#recommendedVariant(wrap);

    if (!(input instanceof HTMLInputElement) || !recommended) {
      // A measurement set the chart has no size for comes back with neither a
      // size nor a variant, and there is nothing for the CTA to add. A size that
      // reaches here named but unresolved is the other case, and worth saying out
      // loud: it means neither the app nor the chart could point at one of this
      // product's variants, which is a chart configured against a size run the
      // product is not sold in.
      const size = wrap instanceof HTMLElement ? wrap.dataset.sgSize?.trim() : undefined;
      if (size) {
        console.warn(
          `[size-guide] the calculator recommends ${size}, and neither the app nor the chart maps it to a variant of this product — no Add to cart shown.`,
        );
      }

      actions.hidden = true;
      return;
    }

    input.value = recommended.id;
    input.disabled = false;

    // Which label the CTA shows; the stylesheet holds the other one back.
    actions.dataset.stock = recommended.available ? 'in' : 'out';

    // Set on the button, which is what `AddToCartComponent#disable` sets and what
    // `ProductFormComponent` reads before it posts anything — so a disabled CTA
    // here stops the add for the same reason a sold-out one does on the product
    // page, and the branded button's own `:disabled` rules give it that face.
    const button = actions.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement) button.disabled = !recommended.available;

    actions.hidden = false;
  }

  /**
   * The product's size run as the snippet rendered it: every size it is sold in,
   * the variant each resolves to, and whether that one can be bought.
   *
   * @returns {Array<{ size: string, id: number, available: boolean }>}
   */
  get #sizeRun() {
    try {
      const run = JSON.parse(this.dataset.sizeRun ?? '[]');

      return Array.isArray(run) ? run : [];
    } catch {
      return [];
    }
  }

  /**
   * The variant the calculator's answer should add, and whether it can be bought.
   *
   * The app's own id first, where it has one. Where it has none — which is what a
   * product whose variants are not sized in the chart's leading column gets, the
   * app naming a size it cannot then look up — the chart it just sent is asked
   * instead, because the pairing is in there.
   *
   * @param {Element | null} wrap - the app's result wrapper
   * @returns {{ id: string, available: boolean } | null}
   */
  #recommendedVariant(wrap) {
    if (!(wrap instanceof HTMLElement)) return null;

    const run = this.#sizeRun;
    const appId = SizeGuide.#VARIANT_ID.exec(wrap.dataset.variantId ?? '')?.[1];

    if (appId) {
      const known = run.find((entry) => String(entry.id) === appId);

      // Stock from the theme, which the app never sends. A variant the run does
      // not carry is offered rather than withheld: better a rejected add, which
      // says so, than a CTA withheld over data this cannot see.
      return { id: appId, available: known ? known.available : true };
    }

    const size = wrap.dataset.sgSize?.trim();
    if (!size) return null;

    const code = this.#sizeCodeFor(size, run);
    if (!code) return null;

    const matches = run.filter((entry) => entry.size === code);

    // One and only one. A product that runs a size in several fits has a variant
    // per fit under the one code, and which fit to add is not this drawer's
    // question — the fit guide is a different conversation.
    const [only] = matches;

    return matches.length === 1 && only ? { id: String(only.id), available: only.available } : null;
  }

  /**
   * The code the product uses for the size the calculator named — "52" for the
   * "3XL" the app answers with on the corduroy trousers.
   *
   * The pairing is a row of the chart: the row the app recommends carries that
   * size in every system the chart runs, and one of those columns is the run the
   * product's own variants are sized in. Which one is read off the values rather
   * than the headings — the headings are the app's words, translated per
   * storefront, while a column either holds this product's size codes or it does
   * not. So each column is scored by how many of its rows name a size the product
   * actually has, and the best-scoring column wins.
   *
   * Scored across every region's table rather than the one the shopper is
   * reading, which is why their Country / Region choice does not decide this. That
   * choice says which system to read the chart *in*; on those trousers a 3XL is a
   * UE 46, an FR 48 and an IT 52, all three of them real sizes of the product, and
   * only the IT column is the run its variants are sized in. Picking by score
   * lands on that column whichever one is on screen.
   *
   * @param {string} size - the size the app named, e.g. "3XL"
   * @param {Array<{ size: string }>} run - the product's own sizes
   * @returns {string | undefined}
   */
  #sizeCodeFor(size, run) {
    const codes = new Set(run.map((entry) => entry.size));
    if (codes.size === 0) return undefined;

    /** @type {string | undefined} */
    let best;
    let bestScore = 0;

    for (const panel of this.querySelectorAll('.sg-region-panel--chart')) {
      const rows = [...panel.querySelectorAll('tbody tr')].map((row) =>
        [...row.querySelectorAll('td')].map(SizeGuide.#reading),
      );

      const recommended = rows.find((row) => row.includes(size));
      if (!recommended) continue;

      for (let column = 0; column < recommended.length; column++) {
        const candidate = recommended[column];
        if (!candidate) continue;

        const score = rows.filter((row) => {
          const value = row[column];

          return value != null && codes.has(value);
        }).length;

        if (score > bestScore) {
          bestScore = score;
          best = candidate;
        }
      }
    }

    // Two rows agreeing is the difference between a column of sizes and a column
    // of measurements that happened to read like one.
    return bestScore >= 2 ? best : undefined;
  }

  /**
   * Shows or clears the calculator's failure line. Its wording is the snippet's,
   * carried in `data-message` — a translated string this file cannot write for
   * itself, and a live region that has to already be in the DOM to announce.
   *
   * @param {boolean} failed
   */
  #showFailure(failed) {
    const message = this.refs.calcMessage;
    if (!(message instanceof HTMLElement)) return;

    message.textContent = failed ? (message.dataset.message ?? '') : '';
  }

  /**
   * Puts the form view back once the drawer has closed.
   *
   * What the shopper typed stays typed: reopening the drawer after a look at the
   * result is usually a correction to one measurement, not a fresh start.
   */
  #reset = () => {
    delete this.dataset.view;
    this.#clearSelectedCell();
    this.#showFailure(false);

    const slot = this.refs.result;
    if (slot instanceof HTMLElement) {
      slot.replaceChildren();
      slot.hidden = true;
    }

    const actions = this.refs.resultActions;
    if (!(actions instanceof HTMLElement)) return;

    actions.hidden = true;
    delete actions.dataset.stock;

    const input = actions.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement) {
      input.value = '';
      input.disabled = true;
    }

    const button = actions.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement) button.disabled = false;
  };
}

if (!customElements.get('size-guide')) {
  customElements.define('size-guide', SizeGuide);
}
